import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import jamf_menu_1 from "./images/jamf_menu_1.png";
import jamf_menu_2 from "./images/jamf_menu_2.png";
import jamf_menu_3 from "./images/jamf_menu_3.png";
import jamf_menu_4 from "./images/jamf_menu_4.png";
import jamf_menu_5 from "./images/jamf_menu_5.png";

const JamfIntegration = () => {
  const [showSecret, setShowSecret] = useState(false);
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const handleClickShowSecret = () => setShowSecret(!showSecret);
  const formInitialValues = {
    url: "",
    credentials: "",
  };
  const formCheckoutSchema = yup.object().shape({
    url: yup.string().required("required"),
    credentials: yup.string().required("required"),
  });
  const handleFormSubmit = (values) => {
    onSubmit(values);
  };
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("jamf");
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [missingScopes, setMissingScopes] = useState([]);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const {
    registerJamfIntegration,
    getJamfRolePermissions,
    checkJamfRolePermissions,
  } = useAPI();
  const colors = useColors();
  const queryClient = useQueryClient();

  useEffect(() => {
    getJamfRolePermissions()
      .then((response) => setRolePermissions(response.data))
      .catch((error) => console.error(error));
  }, []);

  const onSubmit = (values) => {
    setLoading(true);
    try {
      registerJamfIntegration(
        values.url.trim(),
        values.credentials.trim()
      ).then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Jamf integration successful");
        checkJamfRolePermissions()
          .then((response) => {
            const missingPermissions = response.data || [];
            if (missingPermissions.length > 0) {
              setMissingScopes(missingPermissions);
              setIsDialogOpen(true);
            }
          })
          .finally(() => {
            setLoading(false);
          });
      });
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    }
  };

  return (
    <div>
      <Box>
        <Guideline
          title={
            !isIntegrated
              ? "Once integrated, the Breeze platform will automatically read the configurations from Jamf and prioritize the suggested remediation."
              : ""
          }
          image={logos.JamfLogo}
          iconBorderRadius={0}
          name={"Jamf Pro"}
          content={
            loading || isIntegratedLoading ? (
              <LoadingIcon size={25} />
            ) : isIntegrated && !guideMode ? (
              <Box>
                <Typography variant={"h4"}>Jamf Pro is integrated!</Typography>
              </Box>
            ) : (
              <div>
                {/* BUTTONS TO CONTROL EXPANSION */}
                <Box mb="20px">
                  <Grid container spacing={2} direction={"row"}>
                    {guideMode && <IntegrationGuideAlert />}

                    <Grid item>
                      <SecondaryButton onClick={handleExpandAll}>
                        Expand All Screenshots
                      </SecondaryButton>
                    </Grid>
                    <Grid item>
                      <SecondaryButton onClick={handleCollapseAll}>
                        Collapse All Screenshots
                      </SecondaryButton>
                    </Grid>
                  </Grid>
                </Box>
                {/* SETUP INSTRUCTIONS */}
                <Box>
                  <Formik
                    onSubmit={handleFormSubmit}
                    initialValues={{
                      ...formInitialValues,
                      clientId: "",
                      clientSecret: "",
                    }}
                    validationSchema={formCheckoutSchema}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                      setFieldValue,
                    }) => (
                      <Form onSubmit={handleSubmit}>
                        <GuidelineStepByStep>
                          <GuidelineStep title="Login to your Jamf Pro console">
                            <Typography variant={"body2"}>
                              Enter your Tenant URL in Jamf Pro console.
                            </Typography>
                            <Typography variant={"body2"}>
                              Your Jamf Pro console domain should look like
                              this:{" "}
                            </Typography>
                            <Typography variant={"body2"}>
                              https://<strong>tenant-name</strong>
                              .jamfcloud.com.
                            </Typography>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item>
                                <Typography variant="body1">
                                  https://
                                </Typography>
                              </Grid>
                              <Grid item>
                                <TextField
                                  fullWidth
                                  variant="filled"
                                  name="url"
                                  label="Tenant name"
                                  value={values.url}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  error={!!touched.url && !!errors.url}
                                  helperText={touched.url && errors.url}
                                  disabled={guideMode}
                                />
                              </Grid>
                              <Grid item>
                                <Typography variant="body1">
                                  .jamfcloud.com
                                </Typography>
                              </Grid>
                            </Grid>
                            <PrimaryButton
                              onClick={() => {
                                window.open(
                                  `https://${values.url}.jamfcloud.com`,
                                  "_blank"
                                );
                              }}
                              style={{ textTransform: "none" }}
                              disabled={guideMode || !values.url}
                            >
                              Login to Jamf Pro
                            </PrimaryButton>
                          </GuidelineStep>
                          <GuidelineStep title="Create a new API Role">
                            <Typography variant={"body2"}>
                              Click{" "}
                              <Link
                                href={`https://${values.url}.jamfcloud.com/view/settings/system-settings/api-roles-and-clients/api-roles/-1`}
                                target="_blank"
                                sx={{ color: colors.primary_main }}
                                onClick={(e) => {
                                  if (guideMode || !values.url) {
                                    e.preventDefault();
                                  }
                                }}
                                style={{
                                  pointerEvents:
                                    guideMode || !values.url ? "none" : "auto",
                                  color:
                                    guideMode || !values.url
                                      ? colors.grey_500
                                      : colors.primary_main,
                                }}
                              >
                                here
                              </Link>{" "}
                              to create a new API role.
                            </Typography>
                            <Typography variant={"body2"}>
                              Name the new role and give all the read privileges
                              below. Then, click <b>Save</b>.
                            </Typography>
                            <Box
                              sx={{
                                width: "700px",
                                backgroundColor: colors.grey_200,
                                padding: "16px",
                                borderRadius: "8px",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color={colors.primary_darker}
                                gutterBottom
                              >
                                List of Read privileges
                              </Typography>
                              <Typography variant="body2" gutterBottom>
                                Check the box for each completed permission to
                                track the progress
                              </Typography>
                              {rolePermissions
                                .filter(
                                  (permission) =>
                                    permission !==
                                    "Update Local Admin Password Settings"
                                )
                                .map((permission, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <Checkbox />
                                    <Typography>{permission}</Typography>
                                  </Box>
                                ))}
                              {rolePermissions
                                .filter(
                                  (permission) =>
                                    permission ===
                                    "Update Local Admin Password Settings"
                                )
                                .map((permission, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <Checkbox />
                                    <Typography>{permission}*</Typography>
                                  </Box>
                                ))}
                              <Typography
                                variant="body2"
                                color={colors.primary_main}
                                component="span"
                              >
                                *Disclaimer: "Update Local Admin Password
                                Settings" privilege is required to identify if
                                Jamf LAPS is properly configured. Currently,
                                read permission is not enabling to read LAPS
                                configuration.
                              </Typography>
                            </Box>
                            <ScreenshotAccordion
                              images={[
                                {
                                  file: jamf_menu_1,
                                  altext: "Jamf create API role",
                                },
                              ]}
                              onExpanded={addToExpandedList}
                            />
                          </GuidelineStep>
                          <GuidelineStep title="Create a new API Client">
                            <Typography variant={"body2"}>
                              Click{" "}
                              <Link
                                href={`https://${values.url}.jamfcloud.com/view/settings/system-settings/api-roles-and-clients/api-clients/-1`}
                                target="_blank"
                                sx={{ color: colors.primary_main }}
                                onClick={(e) => {
                                  if (guideMode || !values.url) {
                                    e.preventDefault();
                                  }
                                }}
                                style={{
                                  pointerEvents:
                                    guideMode || !values.url ? "none" : "auto",
                                  color:
                                    guideMode || !values.url
                                      ? colors.grey_500
                                      : colors.primary_main,
                                }}
                              >
                                here
                              </Link>{" "}
                              to create a new API client.
                            </Typography>
                            <Typography variant={"body2"}>
                              Name the new client and choose the role you
                              created previously.
                            </Typography>
                            <Typography variant={"body2"}>
                              Leave the “Access Token Lifetime” with the default
                              setting.
                            </Typography>
                            <Typography variant={"body2"}>
                              Then, click on “Enable API Client”.
                            </Typography>
                            <Typography variant={"body2"}>
                              Click “Save”.
                            </Typography>
                            <ScreenshotAccordion
                              images={[
                                {
                                  file: jamf_menu_2,
                                  altext: "Jamf create API client",
                                },
                              ]}
                              onExpanded={addToExpandedList}
                            />
                          </GuidelineStep>
                          <GuidelineStep title="Click “Generate Client Secret” and then click “Create Secret”">
                            <ScreenshotAccordion
                              images={[
                                {
                                  file: jamf_menu_3,
                                  altext: "Jamf API roles and clients",
                                },
                                {
                                  file: jamf_menu_4,
                                  altext: "Jamf API roles and clients",
                                },
                              ]}
                              onExpanded={addToExpandedList}
                            />
                          </GuidelineStep>
                          <GuidelineStep title="Copy the credentials">
                            <Typography variant={"body2"}>
                              Click on "Copy client credentials to clipboard"
                              and paste it below.
                            </Typography>
                            <ScreenshotAccordion
                              images={[
                                {
                                  file: jamf_menu_5,
                                  altext: "Jamf API roles and clients",
                                },
                              ]}
                              onExpanded={addToExpandedList}
                            />
                          </GuidelineStep>
                          <GuidelineStep title="Paste the credentials here">
                            <Typography variant={"body2"}>
                              Paste the credentials JSON that you have copied
                              here.
                            </Typography>
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                            >
                              <TextField
                                fullWidth
                                variant="filled"
                                name="credentials"
                                label="Jamf credentials"
                                placeholder={`{"client_name":"example","client_id":"your-client-id","client_secret":"your-client-secret","grant_type":"client_credentials","content_type":"application/x-www-form-urlencoded"}`}
                                value={values.credentials}
                                onBlur={handleBlur}
                                onChange={(e) => {
                                  handleChange(e);
                                  try {
                                    const parsed = JSON.parse(e.target.value);
                                    if (
                                      parsed.client_id &&
                                      parsed.client_secret
                                    ) {
                                      setFieldValue("isValid", true);
                                      setFieldValue(
                                        "clientId",
                                        parsed.client_id
                                      );
                                      setFieldValue(
                                        "clientSecret",
                                        parsed.client_secret
                                      );
                                    } else {
                                      setFieldValue("isValid", false);
                                      setFieldValue("clientId", "");
                                      setFieldValue("clientSecret", "");
                                    }
                                  } catch {
                                    setFieldValue("isValid", false);
                                    setFieldValue("clientId", "");
                                    setFieldValue("clientSecret", "");
                                  }
                                }}
                                autoComplete="off"
                                type={showSecret ? "text" : "password"}
                                error={
                                  !!touched.credentials && !!errors.credentials
                                }
                                helperText={
                                  touched.credentials && errors.credentials
                                }
                                style={{ width: "700px" }}
                                disabled={guideMode}
                                multiline
                                rows={4}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {values.credentials ? (
                                        values.isValid ? (
                                          <CheckCircleIcon
                                            style={{ color: "green" }}
                                          />
                                        ) : (
                                          <ErrorIcon style={{ color: "red" }} />
                                        )
                                      ) : null}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                              <Box mt={2} width="100%">
                                <TextField
                                  fullWidth
                                  variant="filled"
                                  name="clientId"
                                  label="Client ID"
                                  value={values.clientId}
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  style={{ marginBottom: "16px" }}
                                  disabled
                                />
                                <TextField
                                  fullWidth
                                  variant="filled"
                                  name="clientSecret"
                                  label="Client Secret"
                                  value={values.clientSecret}
                                  type={showSecret ? "text" : "password"}
                                  InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle secret visibility"
                                          onClick={handleClickShowSecret}
                                          onMouseDown={(e) =>
                                            e.preventDefault()
                                          }
                                        >
                                          {showSecret ? (
                                            <Visibility />
                                          ) : (
                                            <VisibilityOff />
                                          )}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  disabled
                                />
                              </Box>
                              <Box mt={2}>
                                <PrimaryButton
                                  type="submit"
                                  disabled={guideMode || !values.isValid}
                                >
                                  Connect
                                </PrimaryButton>
                              </Box>
                            </Box>
                          </GuidelineStep>
                        </GuidelineStepByStep>
                      </Form>
                    )}
                  </Formik>
                </Box>
              </div>
            )
          }
        />
      </Box>
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>Missing Permissions</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please Grant the missing permissions to the API Role:
            {missingScopes.map((scope, index) => (
              <React.Fragment key={index}>
                <br />
                <b>
                  {index + 1}. {scope}
                </b>
              </React.Fragment>
            ))}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JamfIntegration;
